import { injectable } from 'inversify';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { JiraIssuesPatchInput } from '../../../api/endpoints/jira';
import { ObjectManager } from '../../../concepts/objects';
import { ActiveObjectWithEvents, resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { AdapterInfo } from '../../canvas/AdapterInfo';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { AlertHub } from '../alerts/AlertHub';
import { JiraCard } from './JiraCard';
import { CreateJiraInfo, JiraInfo } from './JiraInfo';

export interface AtlassianContextEvents {
    patched: string[];
}

type LoadInput = {
    jiraIssueId: string,
    attempt: number,
};

@injectable()
export class AtlassianContext extends ActiveObjectWithEvents<AtlassianContextEvents> {

    private nextLoadPayload = [] as LoadInput[];
    private loadWorking = false;

    private nextPatchPayload = [] as JiraIssuesPatchInput[];
    private patchWorking = false;

    @resolve(AlertHub) private alerts: AlertHub;
    @resolve(ApiClient) private api: ApiClient;
    @resolve(ProfileModel) private me: ProfileModel;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(EditorSession) private session: EditorSession;

    public start(): void {
        this.burden(
            this.me.observe(this.syncJiraInfo)
        );
        this.syncJiraInfo();
    }

    public ready() {
        return this.me.connections.some(x => x.externalSystemType === 'atlassian-oauth');
    }

    public queueIssueLoad(jiraIssueId: string);
    public queueIssueLoad(jiraIssueIds: string[]);
    public queueIssueLoad(input: any) {
        const jiraIssueIds = Array.isArray(input) ? input : [input];
        this.nextLoadPayload.push(...jiraIssueIds.map(x => ({ jiraIssueId: x, attempt: 0 })));
        this.flushIssueLoads();
    }

    public flushIssueLoads() {
        this.scheduler.timeout('submitIssueLoad', 0,
            () => this.maybeSubmitNextIssueLoad().then(() => { })
        );
    }

    public queueIssuePatch(patch: JiraIssuesPatchInput) {
        patch.patchId = patch.patchId ?? uuid();
        this.nextPatchPayload.push(patch);
        const autoFlush = !this.isUserEditingJiraCard(patch.jiraIssueId);
        if (autoFlush) {
            this.flushIssuePatches(500);
        }
        return patch.patchId;
    }

    public flushIssuePatches(after = 0) {
        this.scheduler.timeout('submitIssuePatch', after,
            () => this.maybeSubmitNextIssuePatch().then(() => { })
        );
    }

    private async maybeSubmitNextIssueLoad() {
        if (this.loadWorking) return;
        if (this.nextLoadPayload.length === 0) return;

        try {
            const board = this.session.get(BoardInfo);
            const payload = this.nextLoadPayload.splice(0, this.nextLoadPayload.length);
            const next = payload
                .filter(x => x.attempt < 5)
                .map(x => x.jiraIssueId);

            if (next.length === 0) return;

            const { failed } = await this.api.exec(v2.jira.issues.load({
                boardId: board.id,
                jiraIssueIds: next,
            }));

            const requeue = payload
                .filter(x => failed.includes(x.jiraIssueId))
                .map(({ jiraIssueId, attempt }) => ({ jiraIssueId, attempt: attempt + 1 }));

            const backoffMult = Math.min(...requeue.map(x => x.attempt)) || 1;
            this.scheduler.timeout(uuid(), 1000 * backoffMult, () => {
                this.nextLoadPayload.push(...requeue);
                this.flushIssueLoads();
            });
        }
        catch (err) {
            console.error('Failed to load Jira issues', err);
        }
        finally {
            this.patchWorking = false;
            this.maybeSubmitNextIssueLoad();
        }
    }

    private async maybeSubmitNextIssuePatch() {
        if (this.patchWorking) return;
        if (this.nextPatchPayload.length === 0) return;

        try {
            const payload = this.nextPatchPayload.splice(0, this.nextPatchPayload.length);
            const result = await this.api.exec(v2.jira.issues.patch(payload));
            if (!result.ok) {
                this.alerts.invoke({
                    type: 'error',
                    body: result.errors.join('\n'),
                    delay: 10000,
                });
            }

            this.events.emit('patched', payload.map(x => x.patchId));
        }
        catch (err) {
            console.error('Failed to patch Jira issues', err);
        }
        finally {
            this.patchWorking = false;
            this.maybeSubmitNextIssuePatch();
        }
    }

    private isUserEditingJiraCard(jiraIssueId: string) {
        const ai = this.session.get(AdapterInfo);
        if (ai.type === 'field') {
            const card = this.objects.getMaybe(ai.target, JiraCard);
            if (!!card && card.externalId === jiraIssueId) {
                return true;
            }
        }
        return false;
    }

    private syncJiraInfo = () => {
        const atlassian = this.me.connections.find(x => x.externalSystemType === 'atlassian-oauth');
        this.session.update(JiraInfo, CreateJiraInfo(!!atlassian));
    };
}

